import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Bookmark, Trash2, MoreVertical } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';
import { db } from '../config/firebase';
import { 
  doc, 
  deleteDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  getDoc, 
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  setDoc,
  deleteField
} from 'firebase/firestore';
import { getInitials } from '../utils/getInitials';
import { formatDistanceToNow } from 'date-fns';
import { createNotification } from '../services/notifications';

interface PostProps {
  post: any;
  onLike?: () => Promise<void>;
  onDelete?: () => Promise<void>;
  onBookmark?: () => Promise<void>;
}

const Post: React.FC<PostProps> = ({ post }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState<any[]>([]);
  const [commentsCount, setCommentsCount] = useState(0);
  const { currentUser } = useFirebase();
  const isAuthor = currentUser?.uid === post.author.id;

  // Load comment count
  useEffect(() => {
    const commentsRef = collection(db, 'posts', post.id, 'comments');
    
    const unsubscribe = onSnapshot(commentsRef, (snapshot) => {
      setCommentsCount(snapshot.size);
    }, (error) => {
      console.error("Error listening to comment count:", error);
    });

    return () => unsubscribe();
  }, [post.id]);

  // Load comments content when expanded
  useEffect(() => {
    if (!showComments) return;

    const commentsRef = collection(db, 'posts', post.id, 'comments');
    const q = query(commentsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setComments(commentsData);
    }, (error) => {
      console.error("Error listening to comments:", error);
    });

    return () => unsubscribe();
  }, [post.id, showComments]);

  // Load likes
  useEffect(() => {
    if (!currentUser) return;
    
    const likesRef = collection(db, 'posts', post.id, 'likes');
    
    const unsubscribe = onSnapshot(likesRef, (snapshot) => {
      const likes = snapshot.docs.map(doc => doc.data());
      setLikesCount(likes.length);
      setHasLiked(snapshot.docs.some(doc => doc.id === currentUser.uid));
    }, (error) => {
      console.error("Error listening to likes:", error);
    });

    return () => unsubscribe();
  }, [post.id, currentUser]);

  const handleLike = async () => {
    if (!currentUser) return;

    try {
      const likeRef = doc(db, 'posts', post.id, 'likes', currentUser.uid);
      const likeDoc = await getDoc(likeRef);
      
      if (likeDoc.exists()) {
        // Unlike
        await deleteDoc(likeRef);
      } else {
        // Like
        await setDoc(likeRef, {
          userId: currentUser.uid,
          displayName: currentUser.displayName || currentUser.email?.split('@')[0] || '',
          createdAt: new Date()
        });

        // Create notification only when liking (not unliking)
        if (!isAuthor) {
          await createNotification(
            'like',
            {
              id: currentUser.uid,
              displayName: currentUser.displayName || currentUser.email?.split('@')[0] || ''
            },
            post.author.id,
            {
              id: post.id,
              content: post.content
            }
          );
        }
      }
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  const handleAddComment = async () => {
    if (!currentUser || !newComment.trim()) return;

    try {
      const commentsRef = collection(db, 'posts', post.id, 'comments');
      const commentData = {
        content: newComment.trim(),
        author: {
          id: currentUser.uid,
          displayName: currentUser.displayName || currentUser.email?.split('@')[0] || '',
          username: currentUser.email?.split('@')[0] || ''
        },
        createdAt: new Date()
      };

      await addDoc(commentsRef, commentData);
      setNewComment('');

      // Create notification for comment
      if (!isAuthor) {
        await createNotification(
          'comment',
          {
            id: currentUser.uid,
            displayName: currentUser.displayName || currentUser.email?.split('@')[0] || ''
          },
          post.author.id,
          {
            id: post.id,
            content: post.content
          }
        );
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleBookmark = async () => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const bookmarks = userData?.bookmarks || [];
      const isBookmarked = bookmarks.includes(post.id);

      await updateDoc(userRef, {
        bookmarks: isBookmarked
          ? arrayRemove(post.id)
          : arrayUnion(post.id)
      });

      // Update local state
      const updatedPost = { ...post, isBookmarked: !isBookmarked };

      // Only create notification if the post author is not the current user
      if (!isAuthor && !isBookmarked) {
        await createNotification(
          'bookmark',
          {
            id: currentUser.uid,
            displayName: currentUser.displayName || currentUser.email?.split('@')[0] || ''
          },
          post.author.id,
          {
            id: post.id,
            content: post.content
          }
        );
      }
    } catch (error) {
      console.error('Error updating bookmark:', error);
    }
  };

  const handleDelete = async () => {
    if (!isAuthor) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this post?');
    if (!confirmDelete) return;

    try {
      // Delete the post from the database
      await deleteDoc(doc(db, 'posts', post.id));

      // Remove the post from the local state
      // setPosts((prevPosts) => prevPosts.filter(p => p.id !== post.id));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const formatPostDate = (date: any) => {
    try {
      if (!date) return 'Unknown date';
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      if (isNaN(dateObj.getTime())) return 'Invalid date';
      return formatDistanceToNow(dateObj, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown date';
    }
  };

  return (
    <div>
      <ul>
        <li>
          <div className="border-b border-gray-800 p-4 hover:bg-gray-800/50">
            <div className="flex space-x-4">
              {/* Author Avatar */}
              <div className="flex-shrink-0">
                <Link to={`/${post.author.username}`}>
                  <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium">
                    {getInitials(post.author.displayName)}
                  </div>
                </Link>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Author Info */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Link to={`/${post.author.username}`}>
                      <span className="font-medium hover:underline">
                        {post.author.displayName}
                      </span>
                    </Link>
                    <span className="text-gray-500">@{post.author.username}</span>
                    <span className="text-gray-500">·</span>
                    <span className="text-gray-500">
                      {formatPostDate(post.createdAt)}
                    </span>
                  </div>

                  {isAuthor && (
                    <div className="relative">
                      <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-2 hover:bg-gray-700/50 rounded-full"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-500" />
                      </button>

                      {showMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-10">
                          <button
                            onClick={handleDelete}
                            className="w-full px-4 py-2 text-left text-red-500 hover:bg-gray-700/50 rounded-lg flex items-center space-x-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Post Content */}
                <p className="mt-2 whitespace-pre-wrap">{post.content}</p>

                {/* Post Image */}
                {post.image && post.image.url && (
                  <div className="mt-3">
                    <img
                      src={post.image.url}
                      alt="Post content"
                      className="rounded-lg max-h-96 w-full object-cover"
                      onError={(e) => {
                        console.error('Image failed to load:', post.image);
                        e.currentTarget.style.display = 'none';
                      }}
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center space-x-8 mt-4">
                  <button
                    onClick={handleLike}
                    className={`flex items-center space-x-2 group ${
                      hasLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                    }`}
                  >
                    <Heart
                      className={`w-5 h-5 ${
                        hasLiked ? 'fill-current' : 'group-hover:fill-current'
                      }`}
                    />
                    <span>{likesCount}</span>
                  </button>

                  <button
                    onClick={() => setShowComments(!showComments)}
                    className="flex items-center space-x-2 text-gray-500 hover:text-blue-500 group"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>{commentsCount} Comments</span>
                  </button>

                  <button
                    onClick={handleBookmark}
                    className={`flex items-center space-x-2 ${
                      post.isBookmarked ? 'text-purple-500' : 'text-gray-500 hover:text-purple-500'
                    }`}
                  >
                    <Bookmark
                      className={`w-5 h-5 ${post.isBookmarked ? 'fill-current' : ''}`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Comments Section */}
          {showComments && (
            <div className="mt-4 space-y-4">
              {/* Comment Input */}
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <button
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Comment
                </button>
              </div>

              {/* Comments List */}
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="flex space-x-2 border-l-2 border-gray-800 pl-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm">
                        {getInitials(comment.author.displayName)}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{comment.author.displayName}</span>
                        <span className="text-gray-500 text-sm">
                          {formatPostDate(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-gray-300">{comment.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </li>
      </ul>
    </div>
  );
};

export default Post;
